import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, Row, Col } from "reactstrap";
import { FormProvider, useForm } from "react-hook-form";
import { Button } from "../../components/Component";
import FormInput from "../form-components/InputComponent";
import FormSelect from "../form-components/SelectComponent";
import FormDatePicker from "../form-components/DatePicker";
import FormTextArea from "../form-components/TextAreaComponent";
import Swal from "sweetalert2";
import customerAdminApis from "../../api/customer-admin/customerAdmin";
import { formatCurrency } from "../../utils/Utils";

interface RecordPaymentModalProps {
  isOpen: boolean;
  toggle: () => void;
  invoiceId: number;
  invoiceNumber: string;
  balanceAmount: number;
  onSuccess: () => void;
}

const paymentMethods = [
  { value: "BankTransfer", label: "Bank Transfer" },
  { value: "Card", label: "Card" },
  { value: "Cash", label: "Cash" },
  { value: "Cheque", label: "Cheque" },
  { value: "NEFT", label: "NEFT" },
  { value: "RTGS", label: "RTGS" },
  { value: "IMPS", label: "IMPS" },
  { value: "UPI", label: "UPI" },
  { value: "Other", label: "Other" },
];

const RecordPaymentModal: React.FC<RecordPaymentModalProps> = ({
  isOpen,
  toggle,
  invoiceId,
  invoiceNumber,
  balanceAmount,
  onSuccess,
}) => {
  const [submitting, setSubmitting] = useState(false);

  const methods = useForm({
    mode: "onChange",
    defaultValues: {
      invoiceNumber: invoiceNumber,
      outstandingBalance: formatCurrency(balanceAmount, "INR"),
      amount: "",
      method: "",
      paymentDate: null as any,
      referenceNumber: "",
      remarks: "",
    },
  });

  const { handleSubmit, reset, setValue } = methods;

  useEffect(() => {
    if (isOpen) {
      setValue("invoiceNumber", invoiceNumber);
      setValue("outstandingBalance", formatCurrency(balanceAmount, "INR"));
    }
  }, [isOpen, invoiceNumber, balanceAmount, setValue]);

  const onSubmit = async (formData: any) => {
    setSubmitting(true);
    try {
      const payload = {
        invoiceId,
        amount: parseFloat(formData.amount),
        method: formData.method,
        paymentDate: formData.paymentDate
          ? formData.paymentDate.format("YYYY-MM-DD")
          : "",
        referenceNumber: formData.referenceNumber || undefined,
        remarks: formData.remarks || undefined,
      };

      const response = await customerAdminApis.recordPayment(payload);

      if (response?.status) {
        await Swal.fire(
          "Success!",
          "Payment recorded successfully. It will be reviewed by the admin.",
          "success"
        );
        reset();
        onSuccess();
        toggle();
      } else {
        await Swal.fire(
          "Error",
          response?.message || "Failed to record payment.",
          "error"
        );
      }
    } catch (error: any) {
      await Swal.fire(
        "Error",
        error?.message || "An error occurred.",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    reset();
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg">
      <ModalHeader toggle={handleClose}>Record Payment</ModalHeader>
      <ModalBody>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Row className="gy-3">
              <Col md="6">
                <FormInput
                  name="invoiceNumber"
                  label="Invoice Number"
                  disabled={true}
                />
              </Col>
              <Col md="6">
                <FormInput
                  name="outstandingBalance"
                  label="Outstanding Balance"
                  disabled={true}
                />
              </Col>
              <Col md="6">
                <FormInput
                  name="amount"
                  label="Amount"
                  type="number"
                  required={true}
                  placeholder="Enter payment amount"
                  min={0.01}
                  customValidation={{
                    validate: (value: string) => {
                      const num = parseFloat(value);
                      if (isNaN(num) || num <= 0)
                        return "Amount must be greater than 0";
                      if (parseFloat(num.toFixed(2)) > parseFloat(Number(balanceAmount).toFixed(2)))
                        return `Amount cannot exceed ${formatCurrency(balanceAmount, "INR")}`;
                      return true;
                    },
                  }}
                />
              </Col>
              <Col md="6">
                <FormSelect
                  name="method"
                  label="Payment Method"
                  required={true}
                  options={paymentMethods}
                  placeholder="Select method"
                />
              </Col>
              <Col md="6">
                <FormDatePicker
                  name="paymentDate"
                  label="Payment Date"
                  required={true}
                  placeholder="Select date"
                />
              </Col>
              <Col md="6">
                <FormInput
                  name="referenceNumber"
                  label="Reference Number"
                  placeholder="Bank reference / transaction ID"
                />
              </Col>
              <Col md="12">
                <FormTextArea
                  name="remarks"
                  label="Remarks"
                  rows={3}
                  placeholder="Additional notes (optional)"
                />
              </Col>
              <Col md="12">
                <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                  <li>
                    <Button
                      color="primary"
                      size="lg"
                      type="submit"
                      disabled={submitting}
                    >
                      {submitting ? "Submitting..." : "Submit Payment"}
                    </Button>
                  </li>
                  <li>
                    <Button
                      type="button"
                      className="btn-dim"
                      color="secondary"
                      size="lg"
                      onClick={handleClose}
                    >
                      Cancel
                    </Button>
                  </li>
                </ul>
              </Col>
            </Row>
          </form>
        </FormProvider>
      </ModalBody>
    </Modal>
  );
};

export default RecordPaymentModal;
